/**
 * IS THE BUNDLE IN THIS PHONE STILL THE ONE ON THE SERVER?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT WENT WRONG, ON 2026-09-09
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Technical Delegate deployed a fix, kept testing on the phone that already had the app
 * open, and went on looking at the OLD bundle — silently, with nothing on screen to say so.
 * There is no service worker in this application (nothing caches the shell on purpose), but
 * a single-page application that is never navigated is never re-fetched either: the tab holds
 * the JavaScript it was handed at first load until somebody reloads the page. During the
 * tournament a UM keeps this app open for DAYS between reloads, so "deploy the fix" and "the
 * fix is on the pitch" can be a week apart, and nobody would know which they were looking at.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SIGNAL, AND WHY IT IS THE RELEASE NAME
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The build already knows which release it is: `infra/deploy.sh` sets `HOCKEYTD_RELEASE` to
 * the release directory's UTC timestamp and `vite.config.ts` compiles it into the bundle as
 * `__RELEASE__` — the string the footer prints. This module compares that compiled-in name
 * with the name in `/version.json`, which the SAME build writes into the dist root from the
 * SAME environment variable (see the `hockeytdVersionFile` plugin in `vite.config.ts`).
 *
 * So there is exactly one identifier, produced once per build, and no second source that
 * could disagree with the footer. A content hash of the bundle would have been a second
 * identifier for the same fact; a version number in a manifest would have been a third thing
 * to remember to bump.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS MODULE REFUSES TO DO
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  · **It never reloads by itself.** A reload while a UM is typing a note into the capture
 *    screen would lose the sentence they were writing, which is a worse fault than the one
 *    being fixed. The tap is the person's, always.
 *  · **It never reports a failure.** A 404 (an older release, deployed before this file
 *    existed), a dead venue wifi, a proxy serving something that is not JSON — all of them
 *    mean the same thing here: *we do not know*, so nothing is drawn and the next check will
 *    ask again. Not knowing the deployed release is not a fault a UM can act on, and the
 *    screen already carries the one status that matters to them (the sync chip).
 *  · **It never polls a hidden tab.** A phone in a pocket at 34°C makes no requests.
 *  · **A bundle that names no release does not poll at all.** A dev server and a bare
 *    `vite build` both compile `dev`, and `dev` cannot honestly be compared with anything.
 */

/** What `vite.config.ts` compiles in when no release name was given to the build. */
export const DEV_RELEASE = "dev";

/** Written into the dist root by the build; served by Caddy as an ordinary file. */
export const VERSION_URL = "/version.json";

/** Five minutes. Long enough to be invisible on a battery, short enough to catch a deploy. */
export const VERSION_POLL_MS = 5 * 60 * 1000;

/**
 * The release name in a `/version.json` body, or `null` when the body is not one.
 *
 * Anything at all may come back from a fetch of a static path — an HTML error page from a
 * hotel captive portal, an empty body, a JSON document of the wrong shape. Every one of them
 * is "we do not know", never a thrown error and never a guess.
 */
export function parseVersionBody(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const release = (body as { release?: unknown }).release;
  if (typeof release !== "string") return null;
  const trimmed = release.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * `GET /version.json`, cache-busted twice over.
 *
 * `cache: "no-store"` tells the browser not to serve or write a cached copy, and the `t`
 * parameter defeats anything between the phone and Caddy that ignores it — a venue proxy, a
 * mobile operator's transparent cache. Reading a cached answer here would be exactly the
 * defect this whole file exists to remove, one layer further out.
 */
export async function fetchDeployedRelease(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_URL}?t=${String(Date.now())}`, { cache: "no-store" });
    if (!response.ok) return null;
    return parseVersionBody(await response.json());
  } catch {
    return null;
  }
}

export interface VersionWatchOptions {
  /** The release compiled into the bundle that is running — `__RELEASE__`. */
  runningRelease: string;
  /** Called once, with the deployed name, the first time it differs from the running one. */
  onNewVersion: (deployedRelease: string) => void;
  /** Seam for the suite. Defaults to {@link fetchDeployedRelease}. */
  fetchDeployed?: () => Promise<string | null>;
  /** Seam for the suite. Defaults to {@link VERSION_POLL_MS}. */
  intervalMs?: number;
}

/**
 * Start watching, and return the teardown.
 *
 * The shape is the one `startNoteQueue` already uses in this app: a function that installs
 * its listeners and hands back the single call that removes all of them, so a re-render can
 * never leave two timers running.
 *
 * **It keeps watching after it has reported**, and reports each DISTINCT deployed name once.
 * A UM who dismisses the bar has said "not now" about the release that was deployed then,
 * not about every release for the rest of the tournament — so the watch has to be able to
 * notice the next one. Re-reporting a name already reported is suppressed, so the caller is
 * told about a deploy, never about a poll.
 */
export function startVersionWatch(options: VersionWatchOptions): () => void {
  const {
    runningRelease,
    onNewVersion,
    fetchDeployed = fetchDeployedRelease,
    intervalMs = VERSION_POLL_MS,
  } = options;

  // A bundle that does not know which release it is cannot tell whether it is stale. Saying
  // nothing is the honest answer, and it keeps a dev server off the network on a timer.
  if (runningRelease === DEV_RELEASE) return () => undefined;

  let stopped = false;
  let checking = false;
  let timer: number | null = null;
  let reported: string | null = null;

  const stopTimer = (): void => {
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  };

  const stop = (): void => {
    stopped = true;
    stopTimer();
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };

  const check = (): void => {
    // One request in flight at a time. Waking a tab that already has a slow check running
    // would otherwise queue a second one behind it on the wifi that is already struggling.
    if (stopped || checking) return;
    checking = true;
    void fetchDeployed()
      .then((deployed) => {
        if (stopped || deployed === null || deployed === runningRelease) return;
        if (deployed === reported) return;
        reported = deployed;
        onNewVersion(deployed);
      })
      .catch(() => {
        // `fetchDeployed` resolves rather than rejects; this is belt and braces so a seam
        // that throws cannot leave an unhandled rejection at a venue.
      })
      .finally(() => {
        checking = false;
      });
  };

  const startTimer = (): void => {
    stopTimer();
    if (stopped) return;
    timer = window.setInterval(check, intervalMs);
  };

  function onVisibilityChange(): void {
    if (document.visibilityState === "hidden") {
      // Nothing is asked of the network while the phone is in a pocket.
      stopTimer();
      return;
    }
    // Coming back to the app is the moment a stale bundle matters most and the moment a
    // person is most likely to accept a reload, so the check is immediate rather than on the
    // next tick of a timer that may be five minutes away.
    check();
    startTimer();
  }

  document.addEventListener("visibilitychange", onVisibilityChange);

  if (document.visibilityState !== "hidden") {
    check();
    startTimer();
  }

  return stop;
}
